// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { type AccountInfo } from '@shared/data-access/interfaces/api/brokerage/account';
import {
  type BrokerPosition,
  type BrokerPositionsAssetType,
  type BrokerPositionsState,
} from '@shared/data-access/interfaces/redux/broker';
import { type AppState } from '@advisor-app/store/store';

export const positionsEntityAdapter = createEntityAdapter<
  BrokerPositionsState,
  string
>({
  selectId: ({ asset }) => asset,
  sortComparer: (current, next) => current.asset.localeCompare(next.asset),
});

export const accountsEntityAdapter = createEntityAdapter<AccountInfo, string>({
  selectId: ({ account_id }) => account_id,
  sortComparer: /* istanbul ignore next */ (current, next) =>
    current.account_id.localeCompare(next.account_id),
});

const positionsSelectors = positionsEntityAdapter.getSelectors();

const accountsSelectors = accountsEntityAdapter.getSelectors();

export const selectBrokerState = ({ broker }: AppState) => broker;

export const selectBroker = createSelector(
  selectBrokerState,
  ({ broker }) => broker,
);

export const selectBrokerConnected = createSelector(
  selectBroker,
  broker => !!broker,
);

export const selectAccountType = createSelector(
  selectBrokerState,
  ({ accountType }) => accountType,
);

export const selectAccount = createSelector(
  selectBrokerState,
  ({ account }) => account,
);

export const selectAccountId = createSelector(
  selectAccount,
  ({ accountId }) => accountId,
);

export const selectOpenLinkAccountModal = createSelector(
  selectBrokerState,
  ({ openLinkAccountModal }) => openLinkAccountModal,
);

export const selectPositions = createSelector(
  selectBrokerState,
  ({ positions }) => positions,
);

export const selectAllPositions = createSelector(selectPositions, positions =>
  positionsSelectors.selectAll(positions).flatMap(assets => assets.positions),
);

export const selectAssets = createSelector(
  selectPositions,
  (positions): Partial<Record<BrokerPositionsAssetType, BrokerPosition[]>> =>
    Object.fromEntries(
      Object.entries(positionsSelectors.selectEntities(positions)).map(
        /* istanbul ignore next */ ([key, value]) => [key, value?.positions ?? []],
      ),
    ),
);

export const selectEtfPositions = createSelector(selectPositions, positions =>
  positionsSelectors.selectById(positions, 'etf'),
);

export const selectStockPositions = createSelector(selectPositions, positions =>
  positionsSelectors.selectById(positions, 'stock'),
);

export const selectMutualFundPositions = createSelector(
  selectPositions,
  positions => positionsSelectors.selectById(positions, 'mutual_fund'),
);

export const selectOtherPositions = createSelector(selectPositions, positions =>
  positionsSelectors.selectById(positions, 'other'),
);

export const selectSummary = createSelector(
  selectBrokerState,
  ({ summary }) => summary,
);

export const selectBrokerApiParams = createSelector(selectBrokerState, state => ({
  brokerConnected: state.broker,
  accountTypeSelect: state.accountType,
  account: state.account,
}));

export const selectAvailableCash = createSelector(
  selectSummary,
  ({ cash }) => cash,
);

export const selectBrokerConfiguration = createSelector(
  selectBrokerState,
  ({ broker, accountType }) => ({
    broker_key: broker,
    type_account: accountType,
  }),
);

export const selectInvestmentsTableData = createSelector(
  selectBrokerState,
  ({ investmentsTableData }) => investmentsTableData,
);

export const selectAccounts = createSelector(selectBrokerState, ({ accounts }) =>
  accountsSelectors.selectAll(accounts),
);

export const selectLoadingAccounts = createSelector(
  selectBrokerState,
  ({ accounts }) => accounts.loading,
);
